<script setup lang="ts">
import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { RiskLevelDisplay } from '@/utils/kyc';

const props = defineProps<{
  overallRisk: RiskLevelDisplay | null;
  systemRisk: RiskLevelDisplay | null;
  riskSource: 'system' | 'finalized_parecer_difference';
  passedChecksCount: number;
  failedChecksCount: number;
}>();

const passedChecksLabel: ComputedRef<string> = computed(() =>
  (props.passedChecksCount === 1 ? 'verificação aprovada' : 'verificações aprovadas'));

const failedChecksLabel: ComputedRef<string> = computed(() =>
  (props.failedChecksCount === 1 ? 'Sinal de Risco' : 'Sinais de Risco'));

</script>

<template>
  <section class="h-full min-h-[176px] rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
    <div class="text-xs font-semibold text-slate-500 uppercase tracking-[0.14em]">Avaliação de Risco</div>
    <template v-if="overallRisk">
      <div class="mt-2 flex items-center gap-2.5">
        <div :class="['w-11 h-11 rounded-lg flex items-center justify-center', overallRisk.bgClass, overallRisk.colorClass]">
          <BaseIcon class="text-2xl">{{ overallRisk.icon }}</BaseIcon>
        </div>
        <div>
          <div :class="['text-3xl leading-none font-semibold', overallRisk.colorClass]">
            {{ overallRisk.label }}
          </div>
          <div class="mt-1 text-sm text-slate-600">
            <template v-if="riskSource === 'finalized_parecer_difference'">
              <span>O parecer final indica um risco diferente do calculado pelo sistema.</span>
            </template>
            <template v-else>
              <span>Calculado pelo sistema</span>
            </template>
          </div>
          <div v-if="riskSource === 'finalized_parecer_difference' && systemRisk" class="mt-1 text-sm text-slate-500">
            Sistema:
            <span :class="['font-semibold', systemRisk.colorClass]">{{ systemRisk.label }}</span>
          </div>
        </div>
      </div>
      <div class="mt-2.5 flex flex-wrap items-center gap-2 text-sm">
        <div class="flex items-center gap-1.5 bg-emerald-50 px-2 py-1 rounded text-emerald-700">
          <BaseIcon class="text-sm">check_circle</BaseIcon>
          <span class="font-semibold">{{ passedChecksCount }}</span>
          <span>{{ passedChecksLabel }}</span>
        </div>
        <div v-if="failedChecksCount > 0" class="flex items-center gap-1.5 bg-rose-50 px-2 py-1 rounded text-rose-700">
          <BaseIcon class="text-sm">cancel</BaseIcon>
          <span class="font-semibold">{{ failedChecksCount }}</span>
          <span>{{ failedChecksLabel }}</span>
        </div>
      </div>
    </template>
    <div v-else class="mt-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5">
      <div class="text-sm font-medium text-slate-700">Nível de risco indisponível</div>
      <div class="mt-0.5 text-xs text-slate-500">Este relatório não retornou score de risco agregado.</div>
    </div>
  </section>
</template>
